import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";

// Inline edit controller supporting two modes:
// 1. Full mode: view/edit targets with save/cancel buttons (for complex forms)
// 2. Simple mode: view/edit targets with blur-to-submit (for single field editing)
export default class extends Controller {
  static targets = ["view", "edit", "form", "cancelButton", "saveButton"];
  static values = {
    url: String,
    method: { type: String, default: "PATCH" },
    refreshUrl: String
  };

  declare readonly viewTarget: HTMLElement;
  declare readonly editTarget: HTMLElement;
  declare readonly formTarget: HTMLFormElement;
  declare readonly hasCancelButtonTarget: boolean;
  declare readonly hasSaveButtonTarget: boolean;
  declare readonly cancelButtonTarget: HTMLButtonElement;
  declare readonly saveButtonTarget: HTMLButtonElement;
  declare readonly urlValue: string;
  declare readonly hasUrlValue: boolean;
  declare readonly methodValue: string;
  declare readonly refreshUrlValue: string;
  declare readonly hasRefreshUrlValue: boolean;

  connect() {
    this.hideEdit();
  }

  edit(event: Event) {
    event.preventDefault();
    this.showEdit();
    this.focusFirstInput();
  }

  cancel(event: Event) {
    event.preventDefault();
    this.hideEdit();
    this.resetForm();
  }

  // Submit via form's native action (for Turbo/standard form submission)
  submit(event: Event) {
    // Don't submit if we're clicking into another element within the edit area
    const relatedTarget = (event as FocusEvent).relatedTarget as HTMLElement;
    if (relatedTarget && this.editTarget.contains(relatedTarget)) {
      return;
    }

    if (!this.formTarget.checkValidity()) {
      this.formTarget.reportValidity();
      return;
    }

    // Submit the form natively (works with Turbo)
    this.formTarget.requestSubmit();
  }

  // Save via fetch API (for custom handling)
  async save(event: Event) {
    event.preventDefault();

    if (!this.formTarget.checkValidity()) {
      this.formTarget.reportValidity();
      return;
    }

    this.setLoadingState(true);

    try {
      const formData = new FormData(this.formTarget);
      const response = await fetch(this.urlValue, {
        method: this.methodValue,
        headers: {
          "X-CSRF-Token": this.getCSRFToken()
        },
        body: formData
      });

      if (response.ok) {
        // For successful updates, hide the edit mode and refresh the view
        this.hideEdit();
        this.resetForm();

        // Refresh the view data if a refresh URL is provided
        if (this.hasRefreshUrlValue && this.refreshUrlValue.trim() !== "") {
          await this.refreshView();
        } else {
          this.showToast(i18n.t('inline_edit.changes_saved'), "success");
        }
      } else {
        // Handle validation errors
        const errorData = await response.json().catch(() => ({}));
        this.showErrors(errorData.errors || {});
      }
    } catch (error) {
      console.error("Error saving inline edit:", error);
      this.showToast(i18n.t('inline_edit.failed_to_save'), "error");
    } finally {
      this.setLoadingState(false);
    }
  }

  private showEdit() {
    this.viewTarget.classList.add("hidden");
    this.editTarget.classList.remove("hidden");
  }

  private hideEdit() {
    this.viewTarget.classList.remove("hidden");
    this.editTarget.classList.add("hidden");
  }

  private focusFirstInput() {
    const firstInput = this.editTarget.querySelector("input, textarea, select");
    if (firstInput instanceof HTMLElement) {
      firstInput.focus();
      // Select all text for easier replacement
      if (firstInput instanceof HTMLInputElement) {
        firstInput.select();
      }
    }
  }

  private resetForm() {
    this.formTarget.reset();
    this.clearErrors();
  }

  private setLoadingState(loading: boolean) {
    if (!this.hasSaveButtonTarget || !this.hasCancelButtonTarget) {
      return;
    }

    this.saveButtonTarget.disabled = loading;
    this.cancelButtonTarget.disabled = loading;

    if (loading) {
      this.saveButtonTarget.textContent = i18n.t('inline_edit.saving');
      this.saveButtonTarget.classList.add("btn-loading");
    } else {
      this.saveButtonTarget.textContent = i18n.t('inline_edit.save');
      this.saveButtonTarget.classList.remove("btn-loading");
    }
  }

  private showErrors(errors: Record<string, string[]>) {
    this.clearErrors();

    Object.entries(errors).forEach(([field, messages]) => {
      const input = this.editTarget.querySelector(
        `[name*="[${field}]"], [name="${field}"]`,
      ) as HTMLInputElement;
      if (input) {
        const errorDiv = document.createElement("div");
        errorDiv.className = "text-red-800 text-sm mt-1";
        errorDiv.textContent = messages.join(", ");
        input.parentNode?.insertBefore(errorDiv, input.nextSibling);
      }
    });
  }

  private clearErrors() {
    this.editTarget
      .querySelectorAll(".text-red-800.text-sm.mt-1")
      .forEach((el) => el.remove());
  }

  private getCSRFToken(): string {
    return (
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") || ""
    );
  }

  private async refreshView() {
    try {
      const response = await fetch(this.refreshUrlValue, {
        headers: {
          "X-CSRF-Token": this.getCSRFToken()
        }
      });

      if (response.ok) {
        const html = await response.text();
        // Replace the view target with the new HTML
        this.viewTarget.innerHTML = html;
      }
    } catch (error) {
      console.error("Error refreshing view:", error);
      this.showToast(i18n.t('inline_edit.failed_to_refresh'), "error");
    }
  }

  private showToast(message: string, type: "success" | "error") {
    // Dispatch a custom event that can be handled by a toast controller
    this.dispatch("toast", { detail: { message, type } });
  }
}
